import { useEffect, useState } from 'react';
import { useAppConfig } from '../../../features/app-config';
import { COMPONENT_IDS } from '../../../features/app-config/constants/appConstants';
import { useHandoverReconciliationSync } from '../../../features/handover-support/hooks/useHandoverReconciliationSync';
import { useEventScheduleSync } from '../../../features/meeting-availability/hooks/useEventScheduleSync';
import { useTaskSync } from '../../../features/tasks/hooks/useTaskSync';

export interface UseDashboardPreparationOptions {
  minimumLoadingTime?: number;
}

export interface UseDashboardPreparationResult {
  isLoading: boolean;
  loadingText: string;
  availableMenuItems: ReturnType<typeof import('../../../features/app-config').useAppConfig>['availableMenuItems'];
  canUsePanelComponent: (componentId: string) => boolean;
}

/**
 * Orchestrates dashboard preparation:
 * 1. Waits for app config (profile/roles/features)
 * 2. Preloads diary data in background (if user has access)
 * 3. Enforces minimum loading time for UX
 */
export const useDashboardPreparation = (
  options: UseDashboardPreparationOptions = {},
): UseDashboardPreparationResult => {
  const { minimumLoadingTime = 5000 } = options;

  const { isLoading: isAppConfigLoading, canUsePanelComponent, availableMenuItems } = useAppConfig();

  // Skip the loading screen on returning visits within the same browser session
  // (e.g. back-button navigation where bfcache is unavailable).
  const [minimumTimeMet, setMinimumTimeMet] = useState(() => sessionStorage.getItem('dashboard.ready') === '1');

  // Preload diary data in background
  // const hasDiaryManagement = canUsePanelComponent(COMPONENT_IDS.DIARY_MANAGEMENT_PANEL);
  // useDiaryPreload({
  //   enabled: !isAppConfigLoading && hasDiaryManagement,
  // });

  // Trigger calendar event sync with the backend
  const hasMeetingAvailability = canUsePanelComponent(COMPONENT_IDS.MEETING_AVAILABILITY_PANEL);
  useEventScheduleSync({
    enabled: !isAppConfigLoading && hasMeetingAvailability,
  });

  // Trigger handover reconciliation sync with the backend
  const hasHandoverReconciliation = canUsePanelComponent(COMPONENT_IDS.HANDOVER_SUPPORT_PANEL);
  useHandoverReconciliationSync(!isAppConfigLoading && hasHandoverReconciliation);

  // Trigger task sync with the backend
  const hasActionItems = canUsePanelComponent(COMPONENT_IDS.ACTION_ITEMS_PANEL);
  useTaskSync({
    enabled: !isAppConfigLoading && hasActionItems,
  });

  // Enforce minimum loading time on first visit; skip on returning visits
  useEffect(() => {
    if (minimumTimeMet) return;
    const timer = setTimeout(() => {
      setMinimumTimeMet(true);
      sessionStorage.setItem('dashboard.ready', '1');
    }, minimumLoadingTime);
    return () => clearTimeout(timer);
  }, [minimumLoadingTime, minimumTimeMet]);

  return {
    isLoading: isAppConfigLoading || !minimumTimeMet,
    loadingText: 'Preparing your dashboard',
    availableMenuItems,
    canUsePanelComponent,
  };
};
